(() => {
  const formatMoney = (cents, currencyCode) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const initReveal = (root) => {
    const nodes = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!nodes.length) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
  };

  const initHero = (root) => {
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    const captions = Array.from(root.querySelectorAll('[data-hero-caption]'));
    const steps = Array.from(root.querySelectorAll('[data-hero-step]'));
    const visual = root.querySelector('.ps-hero__visual');

    if (!slides.length || !steps.length) {
      return;
    }

    const total = slides.length;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let autoplayId = 0;

    const setActive = (index) => {
      activeIndex = ((index % total) + total) % total;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));
      captions.forEach((caption, i) => caption.classList.toggle('is-active', i === activeIndex));
      steps.forEach((step, i) => {
        const isActive = i === activeIndex;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-selected', String(isActive));
      });
    };

    const stopAutoplay = () => {
      if (autoplayId) {
        window.clearInterval(autoplayId);
        autoplayId = 0;
      }
    };

    const startAutoplay = () => {
      if (prefersReducedMotion || total <= 1) {
        return;
      }
      stopAutoplay();
      autoplayId = window.setInterval(() => setActive(activeIndex + 1), 5000);
    };

    steps.forEach((step, index) => {
      step.addEventListener('click', () => {
        setActive(index);
        startAutoplay();
      });
    });

    if (visual) {
      visual.addEventListener('mouseenter', stopAutoplay);
      visual.addEventListener('mouseleave', startAutoplay);
      visual.addEventListener('focusin', stopAutoplay);
      visual.addEventListener('focusout', startAutoplay);
    }

    setActive(0);
    startAutoplay();
  };

  const initProductCarousel = (root) => {
    const track = root.querySelector('[data-products-track]');
    const prev = root.querySelector('[data-products-prev]');
    const next = root.querySelector('[data-products-next]');

    if (!track || !prev || !next) {
      return;
    }

    const getStep = () => {
      const firstCard = track.querySelector('.ps-product-card');
      if (!firstCard) {
        return track.clientWidth;
      }
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return firstCard.getBoundingClientRect().width + gap;
    };

    const updateButtons = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= maxScroll;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  };

  const initDrawer = (root) => {
    const drawer = root.querySelector('[data-checkout-drawer]');
    if (!drawer) {
      return;
    }

    const currencyCode = root.dataset.currency;
    const countEl = root.querySelector('[data-cart-count]');
    const totalEl = root.querySelector('[data-cart-total]');
    const summaryEl = root.querySelector('[data-cart-summary]');

    const syncDrawer = (cart) => {
      const count = Number(cart.item_count || 0);
      drawer.classList.toggle('is-open', count > 0);
      if (countEl) {
        countEl.textContent = String(count);
      }
      if (totalEl) {
        totalEl.textContent = count > 0 ? formatMoney(cart.total_price, currencyCode) : '';
      }
      if (summaryEl) {
        summaryEl.textContent = count === 1 ? 'item in your bag' : 'items in your bag';
      }
    };

    fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart) {
          syncDrawer(cart);
        }
      })
      .catch(() => {});

    root.addEventListener('cart:updated', (event) => {
      if (event.detail?.cart) {
        syncDrawer(event.detail.cart);
      }
    });
  };

  const initCartForms = (root) => {
    const forms = Array.from(root.querySelectorAll('[data-ps-add-form]'));
    const liveRegion = root.querySelector('[data-live-region]');

    if (!forms.length) {
      return;
    }

    forms.forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const redirect = form.dataset.redirect;
        const submitButton = form.querySelector('button[type="submit"]');
        form.setAttribute('aria-busy', 'true');
        if (submitButton) {
          submitButton.disabled = true;
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new URLSearchParams(new FormData(form)).toString(),
            credentials: 'same-origin',
          });

          if (!response.ok) {
            throw new Error('Unable to add item to cart.');
          }

          const addedItem = await response.json();
          const cartResponse = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });

          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            root.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
          }

          if (liveRegion) {
            liveRegion.textContent = `${addedItem.title} added to cart.`;
          }

          if (redirect === 'checkout') {
            window.location.href = '/checkout';
            return;
          }
        } catch (_error) {
          if (liveRegion) {
            liveRegion.textContent = 'There was a problem adding this item to the cart.';
          }
        } finally {
          form.removeAttribute('aria-busy');
          if (submitButton) {
            submitButton.disabled = false;
          }
        }
      });
    });
  };

  const init = (root) => {
    initReveal(root);
    initHero(root);
    initProductCarousel(root);
    initDrawer(root);
    initCartForms(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-product-story]').forEach((root) => init(root));
  });
})();
